import { observer } from 'mobx-react-lite';

import { List, IListProps, ScrollToMode } from '@fluentui/react/lib/List';
import * as React from 'react';
import LoadMoreDataZone, {
    LoadMoreDataComponentProps,
} from 'owa-loadmore-listview/lib/components/LoadMoreDataZone';
import { Spinner } from '@fluentui/react/lib/Spinner';
export { ScrollToMode };
import { registerListRef, removeListRef } from '../utils/ScrollManager';

const SCROLL_DELAY = 30;

export interface ScrollListProps extends IListProps {
    onListScrollWithDelay: (topOfListIndex: number) => void;
    onListScroll: (isAutoScroll: boolean) => void;
}

export interface LoadMoreListProps extends LoadMoreDataComponentProps {
    scenarioId: string;
    listProps: ScrollListProps;
    postRowsComponent?: JSX.Element;
    preRowsComponent?: JSX.Element;
}

export interface LoadMoreListHandle {
    scroll(itemIndex: number, scrollToMode: ScrollToMode): void;
}

const LoadMoreList = observer(
    function LoadMoreList(props: LoadMoreListProps, ref: React.Ref<LoadMoreListHandle>) {
        React.useEffect(() => {
            return () => {
                clearTimeout(onScrollTimeoutTask.current);
            };
        }, []);
        const elementRefs = React.useRef<{
            [key: string]: HTMLDivElement;
        }>({});
        const listRef = React.useRef<React.RefObject<List>>(React.createRef());
        const onScrollTimeoutTask = React.useRef<number>();
        const isAutoScroll = React.useRef<boolean>();
        const requestedScrollIndex = React.useRef<number>();
        const measureItem = (index: number) => {
            if (!elementRefs.current[index]) {
                return 0;
            }
            const height = fetchElementHeight(elementRefs.current[index]);
            return height;
        };
        const onScrollHandler = ev => {
            props.listProps.onListScroll(isAutoScroll.current);
            isAutoScroll.current = false;
            if (onScrollTimeoutTask.current) {
                clearTimeout(onScrollTimeoutTask.current);
            }
            onScrollTimeoutTask.current = (setTimeout(() => {
                onScrollTimeoutTask.current = null;
                onScrollAsyncHandler(ev);
            }, SCROLL_DELAY) as unknown) as number;
        };
        const onScrollAsyncHandler = ev => {
            /** In order to get the correct top item on a list, the _scrollTop in the list must be updated.
             * Give the current implementation this will only update after a scroll if > 1/3 of the page has
             * scrolled since the last upate. We have to call force update to ensure the the start item
             * index retrieved is correct.
             */
            if (requestedScrollIndex.current) {
                /**
                 * if a scroll was just requested, no need to force an update -- we know what index the user previously requested,
                 * so we can short-circuit and just return that index directly. Set back to null so we don't accidentally return it twice
                 * also fixes a partial pixel bug where user requests index n at 110px (for example), control scrolls to top of index n,
                 * but calculates that item at n-1 ends at 110.33px, control would return n-1. If we know we just scrolled to n, just return
                 * the correct value.
                 */
                props.listProps.onListScrollWithDelay(requestedScrollIndex.current);
                requestedScrollIndex.current = null;
                return;
            }
            listRef.current.current.forceUpdate();
            const topOfListIndex = listRef.current.current.getStartItemIndexInView(measureItem);
            props.listProps.onListScrollWithDelay(topOfListIndex);
        };

        React.useImperativeHandle(
            ref,
            () => ({
                scroll(itemIndex: number, scrollToMode: ScrollToMode) {
                    setTimeout(() => {
                        isAutoScroll.current = true;
                        requestedScrollIndex.current = itemIndex;
                        listRef.current.current != null &&
                            listRef.current.current.scrollToIndex(
                                itemIndex,
                                measureItem,
                                scrollToMode
                            );
                    }, 0);
                },
            }),
            []
        );
        const onRenderCell = (
            item?: any,
            index?: number,
            isScrolling?: boolean
        ): React.ReactNode => {
            return (
                <div ref={(ref: HTMLDivElement) => (elementRefs.current[index] = ref)}>
                    {props.listProps.onRenderCell(item, index, isScrolling)}
                </div>
            );
        };
        const renderRows = (rowsToRender: number): JSX.Element => {
            const listProps = {
                ...props.listProps,
                items: props.listProps.items.slice(0, rowsToRender),
            };
            const isLoadInProgress = props.isLoadRowsInProgress && props.getCanLoadMore();
            return (
                <>
                    {props.preRowsComponent}
                    <List
                        {...listProps}
                        componentRef={listRef.current}
                        onRenderCell={onRenderCell}
                    />
                    {props.postRowsComponent}
                    {isLoadInProgress && <Spinner />}
                </>
            );
        };
        return (
            <LoadMoreDataZone
                className={props.className}
                dataSourceId={props.dataSourceId}
                totalRows={props.listProps.items.length}
                onRenderRows={renderRows}
                onLoadMoreRows={props.onLoadMoreRows}
                isLoadRowsInProgress={props.isLoadRowsInProgress}
                getCanLoadMore={props.getCanLoadMore}
                onWillUpdate={props.onWillUpdate}
                onDidUpdate={props.onDidUpdate}
                currentLoadedIndex={props.currentLoadedIndex}
                guardPageCount={props.guardPageCount}
                onScroll={onScrollHandler}
            />
        );
    },
    { forwardRef: true }
);

export function LoadMoreScrollList(props: LoadMoreListProps) {
    const scrollListRef = React.useRef<LoadMoreListHandle>();
    React.useEffect(() => {
        registerListRef(props.scenarioId, scrollListRef);
        return () => {
            removeListRef(props.scenarioId);
        };
    }, []);

    return <LoadMoreList {...props} ref={scrollListRef} />;
}

export default LoadMoreList;

function fetchElementHeight(element: HTMLDivElement) {
    return element.getBoundingClientRect().height;
}
